
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CopyOutlined,
  DownloadOutlined,
  FileSearchOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import API_CONFIG from "../config/api.js";
import apiClient from "../api/apiClient.js";
import { useSelection } from "../SelectionContext";
import { useAuth } from "../context/AuthContext";
import { formatDailyRecapDate } from "../utils/dailyRecapDisplay";
import { parseDailyRecapReportContext } from "../utils/dailyRecapReportLink";
import IncomeStatementStockAdjustments from "./IncomeStatementStockAdjustments";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const REPORTS_API = API_CONFIG.apiUrl("/reports/commercial");
const QUANTITY_DECIMAL_PLACES = 5;
const DATE_ONLY_FORMAT = "YYYY-MM-DD";

const REPORT_TYPE_LABELS = {
  daily_closure: "Cloture journaliere",
  sales_summary: "Ventes multi-periode",
  client_product_sales: "Produits vendus par client",
  profitability: "Rentabilite / marge",
  income_statement: "Compte de resultat",
  stock_valuation: "Valorisation stock",
};

const GLOBAL_REPORT_PERMISSIONS = {
  view: "Gestion Commerciale.Rapports.view",
  generate: "Gestion Commerciale.Rapports.generate",
};

const REPORT_TYPE_PERMISSIONS = {
  daily_closure: {
    view: "Gestion Commerciale.Rapports.ClotureJournaliere.view",
    generate: "Gestion Commerciale.Rapports.ClotureJournaliere.generate",
  },
  sales_summary: {
    view: "Gestion Commerciale.Rapports.VentesMultiPeriode.view",
    generate: "Gestion Commerciale.Rapports.VentesMultiPeriode.generate",
  },
  client_product_sales: {
    view: "Gestion Commerciale.Rapports.ProduitsVendusClients.view",
    generate: "Gestion Commerciale.Rapports.ProduitsVendusClients.generate",
  },
  profitability: {
    view: "Gestion Commerciale.Rapports.Rentabilite.view",
    generate: "Gestion Commerciale.Rapports.Rentabilite.generate",
  },
  income_statement: {
    view: "Gestion Commerciale.Rapports.CompteResultat.view",
    generate: "Gestion Commerciale.Rapports.CompteResultat.generate",
  },
  stock_valuation: {
    view: "Gestion Commerciale.Rapports.ValorisationStock.view",
    generate: "Gestion Commerciale.Rapports.ValorisationStock.generate",
  },
};

const reportTypeLabel = (value) => REPORT_TYPE_LABELS[value] || value;

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${amount.toFixed(2)} XOF`;
  }
};

const formatPercent = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "0%";
  return `${amount.toFixed(2)}%`;
};

const formatQuantity = (value) => {
  const quantity = Number(value || 0);
  if (!Number.isFinite(quantity)) return "0";

  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: QUANTITY_DECIMAL_PLACES,
  }).format(quantity);
};

const renderNetQuantity = (value) => {
  const amount = Number(value || 0);
  const textType = amount < 0 ? "danger" : undefined;
  return <Text type={textType}>{formatQuantity(amount)}</Text>;
};

const formatDateForApi = (date) => {
  if (!date || !dayjs.isDayjs(date) || !date.isValid()) {
    return null;
  }
  return date.format(DATE_ONLY_FORMAT);
};

const CommercialReports = () => {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { hasPermission } = useAuth();
  const location = useLocation();
  const dailyRecapContext = useMemo(
    () => parseDailyRecapReportContext(location.search),
    [location.search]
  );
  const [activeTab, setActiveTab] = useState("daily_closure");
  const [dailyDate, setDailyDate] = useState(() =>
    dailyRecapContext.date ? dayjs(dailyRecapContext.date) : dayjs()
  );
  const [salesRange, setSalesRange] = useState([dayjs().startOf("month"), dayjs()]);
  const [salesGroupBy, setSalesGroupBy] = useState("day");
  const [clientSalesRange, setClientSalesRange] = useState([
    dayjs().startOf("month"),
    dayjs(),
  ]);
  const [clientSalesDetailLevel, setClientSalesDetailLevel] = useState("summary");
  const [clientSalesProductType, setClientSalesProductType] = useState("all");
  const [clientSalesClientIds, setClientSalesClientIds] = useState([]);
  const [profitRange, setProfitRange] = useState([dayjs().startOf("month"), dayjs()]);
  const [incomeRange, setIncomeRange] = useState([dayjs().startOf("month"), dayjs()]);
  const [stockRange, setStockRange] = useState([dayjs().startOf("month"), dayjs()]);
  const [warehouseId, setWarehouseId] = useState(
    dailyRecapContext.warehouseId || selectedWarehouse || null
  );
  const [warehouses, setWarehouses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dailyData, setDailyData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [clientSalesData, setClientSalesData] = useState(null);
  const [profitData, setProfitData] = useState(null);
  const [incomeData, setIncomeData] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [reports, setReports] = useState([]);
  const [loadingPreviewDaily, setLoadingPreviewDaily] = useState(false);
  const [loadingGenerateDaily, setLoadingGenerateDaily] = useState(false);
  const [loadingPreviewSales, setLoadingPreviewSales] = useState(false);
  const [loadingGenerateSales, setLoadingGenerateSales] = useState(false);
  const [loadingPreviewClientSales, setLoadingPreviewClientSales] = useState(false);
  const [loadingGenerateClientSales, setLoadingGenerateClientSales] = useState(false);
  const [loadingPreviewProfit, setLoadingPreviewProfit] = useState(false);
  const [loadingGenerateProfit, setLoadingGenerateProfit] = useState(false);
  const [loadingPreviewIncome, setLoadingPreviewIncome] = useState(false);
  const [loadingGenerateIncome, setLoadingGenerateIncome] = useState(false);
  const [loadingPreviewStock, setLoadingPreviewStock] = useState(false);
  const [loadingGenerateStock, setLoadingGenerateStock] = useState(false);
  const dailyPreviewRef = useRef(null);
  const autoPreviewKeyRef = useRef(null);
  const shouldScrollToDailyPreviewRef = useRef(false);

  const companyId = selectedCompany;

  const canViewReportType = (reportType) => {
    const scopedPermission = REPORT_TYPE_PERMISSIONS[reportType]?.view;
    return (
      hasPermission(GLOBAL_REPORT_PERMISSIONS.view) ||
      (scopedPermission ? hasPermission(scopedPermission) : false)
    );
  };

  const canGenerateReportType = (reportType) => {
    const scopedPermission = REPORT_TYPE_PERMISSIONS[reportType]?.generate;
    return (
      hasPermission(GLOBAL_REPORT_PERMISSIONS.generate) ||
      (scopedPermission ? hasPermission(scopedPermission) : false)
    );
  };

  const visibleReportTabs = useMemo(() => {
    const hasGlobalView = hasPermission(GLOBAL_REPORT_PERMISSIONS.view);
    return Object.keys(REPORT_TYPE_LABELS).filter((reportType) => {
      const scopedPermission = REPORT_TYPE_PERMISSIONS[reportType]?.view;
      return hasGlobalView || (scopedPermission ? hasPermission(scopedPermission) : false);
    });
  }, [hasPermission]);

  const downloadReport = async (reportId, format) => {
    try {
      const params = new URLSearchParams();
      params.set("format", format);
      if (companyId) {
        params.set("companyId", companyId);
      }
      const response = await apiClient.get(
        `${REPORTS_API}/${reportId}/download?${params.toString()}`,
        { responseType: "blob" }
      );

      const contentDisposition = response.headers["content-disposition"] || "";
      const encodedFileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      const plainFileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      let headerFileName = plainFileNameMatch?.[1] || "";
      if (encodedFileNameMatch?.[1]) {
        try {
          headerFileName = decodeURIComponent(encodedFileNameMatch[1]);
        } catch (_) {
          headerFileName = encodedFileNameMatch[1];
        }
      }
      const fileName =
        headerFileName.trim() ||
        `rapport_${reportId}.${format === "excel" ? "xlsx" : "pdf"}`;

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      message.error("Telechargement impossible");
    }
  };

  const dailySummaryCards = useMemo(() => {
    if (!dailyData?.summary) return [];
    const summary = dailyData.summary;
    return [
      { label: "Ventes totales", value: formatCurrency(summary.totalSales) },
      { label: "Livraison facturée en ligne", value: summary.totalDeliveryEcommerce == null ? "—" : formatCurrency(summary.totalDeliveryEcommerce) },
      { label: "Achats", value: formatCurrency(summary.totalPurchases) },
      { label: "Depenses", value: formatCurrency(summary.totalExpenses) },
      { label: "Encaissements", value: formatCurrency(summary.totalPaymentsIn) },
      { label: "Decaissements", value: formatCurrency(summary.totalPaymentsOut) },
      { label: "Flux net", value: formatCurrency(summary.netCashFlow) },
    ];
  }, [dailyData]);

  const dailyHasActivity = useMemo(() => {
    if (!dailyData) return null;

    const numericGroups = [
      dailyData.summary,
      dailyData.counts,
      dailyData.production,
    ];
    const hasNonZeroMetric = numericGroups.some((group) =>
      Object.values(group || {}).some((value) => {
        const number = Number(value);
        return Number.isFinite(number) && number !== 0;
      })
    );
    const rowGroups = [
      dailyData.sales?.products,
      dailyData.sales?.topCustomers,
      dailyData.purchases?.products,
      dailyData.purchases?.topSuppliers,
      dailyData.adjustments?.products,
      dailyData.expenses?.byCategory,
      dailyData.production?.outputs,
      dailyData.stock?.byMovement,
      dailyData.payments?.inByMode,
      dailyData.payments?.outByMode,
    ];

    return hasNonZeroMetric || rowGroups.some((rows) => rows?.length > 0);
  }, [dailyData]);

  const salesSummaryCards = useMemo(() => {
    if (!salesData?.summary) return [];
    const summary = salesData.summary;
    return [
      { label: "Ventes classiques", value: formatCurrency(summary.totalClassic) },
      { label: "Ventes e-commerce", value: formatCurrency(summary.totalEcommerce) },
      { label: "Livraison facturée en ligne", value: summary.totalDeliveryEcommerce == null ? "—" : formatCurrency(summary.totalDeliveryEcommerce) },
      { label: "Total facturé e-commerce", value: summary.totalInvoicedEcommerce == null ? "—" : formatCurrency(summary.totalInvoicedEcommerce) },
      { label: "Total ventes", value: formatCurrency(summary.totalOverall) },
      { label: "Nb ventes", value: summary.totalCount },
    ];
  }, [salesData]);

  const clientSalesSummaryCards = useMemo(() => {
    if (!clientSalesData?.summary) return [];
    const summary = clientSalesData.summary;
    return [
      { label: "Lignes", value: summary.totalLines || 0 },
      { label: "Clients", value: summary.totalClients || 0 },
      { label: "Produits", value: summary.totalProducts || 0 },
      { label: "Quantite totale", value: formatQuantity(summary.totalQuantity || 0) },
      { label: "Montant total", value: formatCurrency(summary.totalAmount || 0) },
    ];
  }, [clientSalesData]);

  const profitSummaryCards = useMemo(() => {
    if (!profitData?.summary) return [];
    const summary = profitData.summary;
    return [
      { label: "Chiffre d'affaires", value: formatCurrency(summary.totalRevenue) },
      { label: "Cout des revenus", value: formatCurrency(summary.totalCogs) },
      { label: "Marge brute", value: formatCurrency(summary.totalMargin) },
      { label: "Taux de marge brute", value: formatPercent(summary.marginRate) },
    ];
  }, [profitData]);

  const stockSummaryCards = useMemo(() => {
    if (!stockData?.summary) return [];
    const summary = stockData.summary;
    return [
      { label: "Valeur stock (cout)", value: formatCurrency(summary.totalStockValueCost) },
      { label: "Valeur stock (vente)", value: formatCurrency(summary.totalStockValueSale) },
      { label: "Stock total", value: formatQuantity(summary.totalStockQuantity) },
      { label: "Ventes periode", value: formatQuantity(summary.totalSoldQuantity) },
      { label: "Rotation", value: formatPercent(summary.rotationRate) },
    ];
  }, [stockData]);

  const incomeSummaryCards = useMemo(() => {
    if (!incomeData?.summary) return [];
    const summary = incomeData.summary;
    return [
      { label: "Produits ordinaires nets", value: formatCurrency(summary.ordinaryProducts) },
      { label: "Revenus des ventes hors livraison", value: summary.ordinarySalesProducts == null ? "—" : formatCurrency(summary.ordinarySalesProducts) },
      { label: "Recettes nettes de livraison", value: summary.deliveryRevenue == null ? "—" : formatCurrency(summary.deliveryRevenue) },
      {
        label: "Cout des revenus",
        value: formatCurrency(summary.costOfRevenue ?? summary.directCharges),
      },
      { label: "Marge brute", value: formatCurrency(summary.grossResult) },
      { label: "Taux marge brute", value: formatPercent(summary.grossMarginRate) },
      { label: "Charges d'exploitation", value: formatCurrency(summary.operatingExpenses) },
      { label: "Resultat d'exploitation", value: formatCurrency(summary.operatingResult) },
      { label: "Resultat net", value: formatCurrency(summary.netResult) },
      {
        label: "Couverture couts traces",
        value: formatPercent(
          summary.costCoverage?.traceableCostLineRate ??
            summary.costCoverage?.snapshotLineRate ??
            summary.merchandiseSnapshotCoverageRate ??
            0
        ),
      },
    ];
  }, [incomeData]);

  const fetchWarehouses = async () => {
    if (!companyId) return;
    try {
      const response = await apiClient.get(
        `${API_CONFIG.apiUrl("/warehouses")}?company_id=${companyId}&limit=200`
      );
      setWarehouses(response.data.warehouses || []);
    } catch (error) {
      message.error("Impossible de charger les établissements");
    }
  };

  const fetchCustomers = async () => {
    if (!companyId) return;
    try {
      const params = new URLSearchParams();
      params.set("companyId", companyId);
      if (warehouseId) {
        params.set("warehouseId", warehouseId);
      }
      const response = await apiClient.get(
        `${API_CONFIG.apiUrl("/users/customers")}?${params.toString()}`
      );
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error("Impossible de charger les clients");
    }
  };

  const fetchReports = async () => {
    if (!companyId) return;
    try {
      const params = new URLSearchParams();
      params.set("companyId", companyId);
      if (warehouseId) params.set("warehouseId", warehouseId);
      if (activeTab) params.set("reportType", activeTab);
      const response = await apiClient.get(`${REPORTS_API}?${params.toString()}`);
      setReports(response.data.data || []);
    } catch (error) {
      message.error("Impossible de charger les rapports");
    }
  };

  const insertGeneratedReportInHistory = (reportId, reportType, reportDate) => {
    if (!reportId || !reportType) return;
    const safeReportDate = reportDate || dayjs().format("YYYY-MM-DD");
    setReports((prev) => {
      const filtered = prev.filter((item) => item.id !== reportId);
      return [
        {
          id: reportId,
          report_type: reportType,
          report_date: safeReportDate,
          status: "completed",
          created_at: new Date().toISOString(),
          detail_level:
            reportType === "client_product_sales" ? clientSalesDetailLevel : null,
        },
        ...filtered,
      ];
    });
  };

  const previewDailyClosure = useCallback(async () => {
    if (!companyId) {
      message.warning("Selectionnez une entreprise.");
      return false;
    }
    const reportDate = formatDateForApi(dailyDate);
    if (!reportDate) {
      message.warning("Selectionnez une date valide.");
      return false;
    }
    setLoadingPreviewDaily(true);
    try {
      const params = new URLSearchParams();
      params.set("companyId", companyId);
      params.set("date", reportDate);
      if (warehouseId) params.set("warehouseId", warehouseId);

      const response = await apiClient.get(
        `${REPORTS_API}/daily-closure?${params.toString()}`
      );
      setDailyData(response.data.data || null);
      return true;
    } catch (error) {
      message.error("Erreur lors du chargement du rapport");
      shouldScrollToDailyPreviewRef.current = false;
      return false;
    } finally {
      setLoadingPreviewDaily(false);
    }
  }, [companyId, dailyDate, warehouseId]);

  const generateDailyClosure = async () => {
    if (!companyId) {
      message.warning("Selectionnez une entreprise.");
      return;
    }
    const reportDate = formatDateForApi(dailyDate);
    if (!reportDate) {
      message.warning("Selectionnez une date valide.");
      return;
    }
    setLoadingGenerateDaily(true);
    try {
      const payload = {
        companyId,
        warehouseId,
        date: reportDate,
      };
      const response = await apiClient.post(`${REPORTS_API}/daily-closure/generate`, payload);
      setDailyData(response.data.data || null);
      if (response.data.skipped) {
        message.info(
          response.data.message ||
            "Aucun changement detecte. Le dernier rapport existant est reutilise."
        );
        await fetchReports();
        if (response.data.reportId) {
          await downloadReport(response.data.reportId, "pdf");
        }
        return;
      }
      message.success(response.data.message || "Rapport genere avec succes");
      await fetchReports();
    } catch (error) {
      message.error("Erreur lors de la generation du rapport");
    } finally {
      setLoadingGenerateDaily(false);
    }
  };
  const previewSalesSummary = async () => {
    if (!companyId) {
      message.warning("Selectionnez une entreprise.");
      return;
    }
    if (!salesRange?.[0] || !salesRange?.[1]) {
      message.warning("Selectionnez une periode.");
      return;
    }
    setLoadingPreviewSales(true);
    try {
      const params = new URLSearchParams();
      params.set("companyId", companyId);
      params.set("startDate", salesRange[0].format("YYYY-MM-DD"));
      params.set("endDate", salesRange[1].format("YYYY-MM-DD"));
      params.set("groupBy", salesGroupBy);
      if (warehouseId) params.set("warehouseId", warehouseId);

      const response = await apiClient.get(
        `${REPORTS_API}/sales-summary?${params.toString()}`
      );
      setSalesData(response.data.data || null);
    } catch (error) {
      message.error("Erreur lors du chargement du rapport");
    } finally {
      setLoadingPreviewSales(false);
    }
  };

  const generateSalesSummary = async () => {
    if (!companyId) {
      message.warning("Selectionnez une entreprise.");
      return;
    }
    if (!salesRange?.[0] || !salesRange?.[1]) {
      message.warning("Selectionnez une periode.");
      return;
    }
    setLoadingGenerateSales(true);
    try {
      const payload = {
        companyId,
        warehouseId,
        startDate: salesRange[0].format("YYYY-MM-DD"),
        endDate: salesRange[1].format("YYYY-MM-DD"),
        groupBy: salesGroupBy,
      };
      const response = await apiClient.post(`${REPORTS_API}/sales-summary/generate`, payload);
      setSalesData(response.data.data || null);
      message.success("Rapport genere avec succes");
      await fetchReports();
    } catch (error) {
      message.error("Erreur lors de la generation du rapport");
    } finally {
      setLoadingGenerateSales(false);
    }
  };

  const previewClientProductSales = async () => {
    if (!companyId) {
      message.warning("Selectionnez une entreprise.");
      return;
    }
    if (!clientSalesRange?.[0] || !clientSalesRange?.[1]) {
      message.warning("Selectionnez une periode.");
      return;
    }
    setLoadingPreviewClientSales(true);
    try {
      const params = new URLSearchParams();
      params.set("companyId", companyId);
      params.set("startDate", clientSalesRange[0].format("YYYY-MM-DD"));
      params.set("endDate", clientSalesRange[1].format("YYYY-MM-DD"));
      params.set("detailLevel", clientSalesDetailLevel);
      if (warehouseId) params.set("warehouseId", warehouseId);
      if (clientSalesClientIds.length > 0) {
        params.set("clientIds", clientSalesClientIds.join(","));
      }
      if (clientSalesProductType && clientSalesProductType !== "all") {
        params.set("productType", clientSalesProductType);
      }

      const response = await apiClient.get(
        `${REPORTS_API}/client-product-sales?${params.toString()}`
      );
      setClientSalesData(response.data.data || null);
    } catch (error) {
      message.error("Erreur lors du chargement du rapport");
    } finally {
      setLoadingPreviewClientSales(false);
    }
  };

  const generateClientProductSales = async () => {
    if (!companyId) {
      message.warning("Selectionnez une entreprise.");
      return;
    }
    if (!clientSalesRange?.[0] || !clientSalesRange?.[1]) {
      message.warning("Selectionnez une periode.");
      return;
    }
    setLoadingGenerateClientSales(true);
    try {
      const payload = {
        companyId,
        warehouseId,
        startDate: clientSalesRange[0].format("YYYY-MM-DD"),
        endDate: clientSalesRange[1].format("YYYY-MM-DD"),
        detailLevel: clientSalesDetailLevel,
        clientIds: clientSalesClientIds,
      };
      if (clientSalesProductType && clientSalesProductType !== "all") {
        payload.productType = clientSalesProductType;
      }

      const response = await apiClient.post(
        `${REPORTS_API}/client-product-sales/generate`,
        payload
      );
      setClientSalesData(response.data.data || null);
      insertGeneratedReportInHistory(
        response.data.reportId,
        "client_product_sales",
        response.data?.data?.meta?.endDate
      );
      message.success("Rapport genere avec succes");
      await fetchReports();
    } catch (error) {
      message.error("Erreur lors de la generation du rapport");
    } finally {
      setLoadingGenerateClientSales(false);
    }
  };

  const previewProfitability = async () => {
    if (!companyId) {
      message.warning("Selectionnez une entreprise.");
      return;
    }
    if (!profitRange?.[0] || !profitRange?.[1]) {
      message.warning("Selectionnez une periode.");
      return;
    }
    setLoadingPreviewProfit(true);
    try {
      const params = new URLSearchParams();
      params.set("companyId", companyId);
      params.set("startDate", profitRange[0].format("YYYY-MM-DD"));
      params.set("endDate", profitRange[1].format("YYYY-MM-DD"));
      if (warehouseId) params.set("warehouseId", warehouseId);

      const response = await apiClient.get(
        `${REPORTS_API}/profitability?${params.toString()}`
      );
      setProfitData(response.data.data || null);
    } catch (error) {
      message.error("Erreur lors du chargement du rapport");
    } finally {
      setLoadingPreviewProfit(false);
    }
  };

  const generateProfitability = async () => {
    if (!companyId) {
      message.warning("Selectionnez une entreprise.");
      return;
    }
    if (!profitRange?.[0] || !profitRange?.[1]) {
      message.warning("Selectionnez une periode.");
      return;
    }
    setLoadingGenerateProfit(true);
    try {
      const payload = {
        companyId,
        warehouseId,
        startDate: profitRange[0].format("YYYY-MM-DD"),
        endDate: profitRange[1].format("YYYY-MM-DD"),
      };
      const response = await apiClient.post(`${REPORTS_API}/profitability/generate`, payload);
      setProfitData(response.data.data || null);
      message.success("Rapport genere avec succes");
      await fetchReports();
    } catch (error) {
      message.error("Erreur lors de la generation du rapport");
    } finally {
      setLoadingGenerateProfit(false);
    }
  };

  const previewIncomeStatement = async () => {
    if (!companyId) {
      message.warning("Selectionnez une entreprise.");
      return;
    }
    if (!incomeRange?.[0] || !incomeRange?.[1]) {
      message.warning("Selectionnez une periode.");
      return;
    }
    setLoadingPreviewIncome(true);
    try {
      const params = new URLSearchParams();
      params.set("companyId", companyId);
      params.set("startDate", incomeRange[0].format("YYYY-MM-DD"));
      params.set("endDate", incomeRange[1].format("YYYY-MM-DD"));
      if (warehouseId) params.set("warehouseId", warehouseId);

      const response = await apiClient.get(
        `${REPORTS_API}/income-statement?${params.toString()}`
      );
      setIncomeData(response.data.data || null);
    } catch (error) {
      message.error("Erreur lors du chargement du compte de resultat");
    } finally {
      setLoadingPreviewIncome(false);
    }
  };

  const generateIncomeStatement = async () => {
    if (!companyId) {
      message.warning("Selectionnez une entreprise.");
      return;
    }
    if (!incomeRange?.[0] || !incomeRange?.[1]) {
      message.warning("Selectionnez une periode.");
      return;
    }
    setLoadingGenerateIncome(true);
    try {
      const payload = {
        companyId,
        warehouseId,
        startDate: incomeRange[0].format("YYYY-MM-DD"),
        endDate: incomeRange[1].format("YYYY-MM-DD"),
      };
      const response = await apiClient.post(
        `${REPORTS_API}/income-statement/generate`,
        payload
      );
      setIncomeData(response.data.data || null);
      message.success("Compte de resultat genere avec succes");
      await fetchReports();
    } catch (error) {
      message.error("Erreur lors de la generation du compte de resultat");
    } finally {
      setLoadingGenerateIncome(false);
    }
  };

  const previewStockValuation = async () => {
    if (!companyId) {
      message.warning("Selectionnez une entreprise.");
      return;
    }
    if (!stockRange?.[0] || !stockRange?.[1]) {
      message.warning("Selectionnez une periode.");
      return;
    }
    setLoadingPreviewStock(true);
    try {
      const params = new URLSearchParams();
      params.set("companyId", companyId);
      params.set("startDate", stockRange[0].format("YYYY-MM-DD"));
      params.set("endDate", stockRange[1].format("YYYY-MM-DD"));
      if (warehouseId) params.set("warehouseId", warehouseId);

      const response = await apiClient.get(
        `${REPORTS_API}/stock-valuation?${params.toString()}`
      );
      setStockData(response.data.data || null);
    } catch (error) {
      message.error("Erreur lors du chargement du rapport");
    } finally {
      setLoadingPreviewStock(false);
    }
  };

  const generateStockValuation = async () => {
    if (!companyId) {
      message.warning("Selectionnez une entreprise.");
      return;
    }
    if (!stockRange?.[0] || !stockRange?.[1]) {
      message.warning("Selectionnez une periode.");
      return;
    }
    setLoadingGenerateStock(true);
    try {
      const payload = {
        companyId,
        warehouseId,
        startDate: stockRange[0].format("YYYY-MM-DD"),
        endDate: stockRange[1].format("YYYY-MM-DD"),
      };
      const response = await apiClient.post(`${REPORTS_API}/stock-valuation/generate`, payload);
      setStockData(response.data.data || null);
      message.success("Rapport genere avec succes");
      await fetchReports();
    } catch (error) {
      message.error("Erreur lors de la generation du rapport");
    } finally {
      setLoadingGenerateStock(false);
    }
  };
  const copyTextToClipboard = async (text) => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const tempInput = document.createElement("input");
    tempInput.value = text;
    tempInput.setAttribute("readonly", "");
    tempInput.style.position = "absolute";
    tempInput.style.left = "-9999px";
    document.body.appendChild(tempInput);
    tempInput.select();
    document.execCommand("copy");
    document.body.removeChild(tempInput);
  };

  const copySecureShareLink = async (reportId) => {
    try {
      const response = await apiClient.post(`${REPORTS_API}/${reportId}/share-link`, {
        companyId,
        format: "pdf",
      });

      const shareUrl = response?.data?.data?.shareUrl;
      const expiresAt = response?.data?.data?.expiresAt;

      if (!shareUrl) {
        throw new Error("Lien de partage indisponible");
      }

      await copyTextToClipboard(shareUrl);

      const expirationLabel = expiresAt ? dayjs(expiresAt).format("DD/MM/YYYY HH:mm") : null;
      message.success(
        expirationLabel
          ? `Lien securise copie (expire le ${expirationLabel})`
          : "Lien securise copie"
      );
    } catch (error) {
      message.error("Erreur lors de la copie du lien securise");
    }
  };

  useEffect(() => {
    setWarehouseId(
      dailyRecapContext.warehouseId || selectedWarehouse || null
    );
  }, [dailyRecapContext.warehouseId, selectedWarehouse]);

  useEffect(() => {
    if (dailyRecapContext.date) {
      setDailyDate(dayjs(dailyRecapContext.date));
    }
  }, [dailyRecapContext.date]);

  useEffect(() => {
    if (!visibleReportTabs.length) return;
    if (!visibleReportTabs.includes(activeTab)) {
      setActiveTab(visibleReportTabs[0]);
    }
  }, [activeTab, visibleReportTabs]);

  useEffect(() => {
    fetchWarehouses();
    fetchCustomers();
    fetchReports();
  }, [companyId, warehouseId, activeTab]);

  useEffect(() => {
    if (
      !dailyRecapContext.autoPreview ||
      !companyId ||
      activeTab !== "daily_closure" ||
      formatDateForApi(dailyDate) !== dailyRecapContext.date ||
      (dailyRecapContext.warehouseId &&
        Number(warehouseId) !== dailyRecapContext.warehouseId)
    ) {
      return;
    }

    const previewKey = [
      companyId,
      dailyRecapContext.date,
      dailyRecapContext.warehouseId || "all",
    ].join(":");
    if (autoPreviewKeyRef.current === previewKey) return;

    autoPreviewKeyRef.current = previewKey;
    shouldScrollToDailyPreviewRef.current = true;
    setDailyData(null);
    void previewDailyClosure();
  }, [
    activeTab,
    companyId,
    dailyDate,
    dailyRecapContext.autoPreview,
    dailyRecapContext.date,
    dailyRecapContext.warehouseId,
    previewDailyClosure,
    warehouseId,
  ]);

  useEffect(() => {
    if (!dailyData || !shouldScrollToDailyPreviewRef.current) return;
    shouldScrollToDailyPreviewRef.current = false;

    const scrollToPreview = () =>
      dailyPreviewRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
      });

    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(scrollToPreview);
    } else {
      scrollToPreview();
    }
  }, [dailyData]);

  useEffect(() => {
    if (!clientSalesClientIds.length) return;
    const allowedIds = new Set(customers.map((customer) => Number(customer.id)));
    const filteredIds = clientSalesClientIds.filter((id) => allowedIds.has(Number(id)));
    if (filteredIds.length !== clientSalesClientIds.length) {
      setClientSalesClientIds(filteredIds);
    }
  }, [customers, clientSalesClientIds]);

  const reportColumns = [
    {
      title: "Date rapport",
      dataIndex: "report_date",
      key: "report_date",
      render: (value) => dayjs(value).format("YYYY-MM-DD"),
    },
    {
      title: "Genere le",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-"),
    },
    {
      title: "Type",
      dataIndex: "report_type",
      key: "report_type",
      render: (value) => <Tag color="blue">{reportTypeLabel(value)}</Tag>,
    },
    {
      title: "Mode",
      dataIndex: "detail_level",
      key: "detail_level",
      render: (value, record) => {
        if (record.report_type !== "client_product_sales") {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Tag color={value === "detailed" ? "purple" : "cyan"}>
            {value === "detailed" ? "Detaille" : "Resume"}
          </Tag>
        );
      },
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (value) => <Tag color={value === "completed" ? "green" : "orange"}>{value}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        if (!canViewReportType(record.report_type)) {
          return <Tag color="red">Acces refuse</Tag>;
        }
        return (
          <Space>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadReport(record.id, "pdf")}
            >
              PDF
            </Button>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadReport(record.id, "excel")}
            >
              Excel
            </Button>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copySecureShareLink(record.id)}
            >
              Copier lien securise
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Rapports - Gestion commerciale</Title>
      <Text type="secondary">
        Analysez vos ventes, marges et stock. Chaque rapport peut etre genere en PDF/Excel.
      </Text>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginTop: 16 }}
        items={[
          {
            key: "daily_closure",
            label: "Cloture journaliere",
            children: (
              <>
                {dailyRecapContext.requested && (
                  <Alert
                    showIcon
                    type="info"
                    style={{ marginBottom: 16 }}
                    message={`Détail de la clôture du ${formatDailyRecapDate(
                      dailyRecapContext.date
                    )}`}
                    description={
                      dailyRecapContext.autoPreview
                        ? "La date et l'établissement du bilan ont été repris automatiquement. L’aperçu détaillé est chargé ci-dessous."
                        : "La clôture demandée est sélectionnée. Utilisez Aperçu pour afficher son détail."
                    }
                  />
                )}
                <Card>
                  <Row gutter={16} align="middle">
                    <Col xs={24} md={6}>
                      <Text strong>Date</Text>
                      <DatePicker
                        style={{ width: "100%", marginTop: 4 }}
                        value={dailyDate}
                        onChange={(value) => {
                          if (dayjs.isDayjs(value) && value.isValid()) {
                            setDailyData(null);
                            setDailyDate(value);
                          }
                        }}
                        format={DATE_ONLY_FORMAT}
                        allowClear={false}
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <Text strong>Établissement</Text>
                      <Select
                        style={{ width: "100%", marginTop: 4 }}
                        value={warehouseId || "all"}
                        onChange={(value) => {
                          setDailyData(null);
                          setWarehouseId(value === "all" ? null : value);
                        }}
                      >
                        <Option value="all">Tous les établissements</Option>
                        {warehouses.map((warehouse) => (
                          <Option key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} md={12}>
                      <Space style={{ marginTop: 22 }}>
                        <Button
                          type="default"
                          icon={<FileSearchOutlined />}
                          loading={loadingPreviewDaily}
                          onClick={previewDailyClosure}
                        >
                          Apercu
                        </Button>
                        {canGenerateReportType("daily_closure") && (
                          <Button
                            type="primary"
                            icon={<FileTextOutlined />}
                            loading={loadingGenerateDaily}
                            onClick={generateDailyClosure}
                          >
                            Generer PDF/Excel
                          </Button>
                        )}
                      </Space>
                    </Col>
                  </Row>
                </Card>

                {dailyData && (
                  <div ref={dailyPreviewRef}>
                  <Card style={{ marginTop: 16 }} title="Synthèse de la clôture">
                    {dailyHasActivity === false ? (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                          <span>
                            <strong>Aucune opération enregistrée</strong>
                            <br />
                            Aucun mouvement commercial, financier, de stock ou de
                            production n’a été trouvé pour cette date et cet établissement.
                          </span>
                        }
                      />
                    ) : (
                      <>
                    <Row gutter={16}>
                      {dailySummaryCards.map((item) => (
                        <Col xs={12} md={4} key={item.label} style={{ marginBottom: 12 }}>
                          <Card>
                            <Text type="secondary">{item.label}</Text>
                            <div style={{ fontSize: 16, fontWeight: 600 }}>{item.value}</div>
                          </Card>
                        </Col>
                      ))}
                    </Row>

                    <Divider />

                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Title level={5}>Vente du jour</Title>
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={dailyData.sales?.products || []}
                          rowKey={(record) => `${record.product_id}-${record.product_name}`}
                          columns={[
                            { title: "Produit", dataIndex: "product_name", key: "product_name" },
                            {
                              title: "Quantite",
                              dataIndex: "quantity",
                              key: "quantity",
                              render: (value) => formatQuantity(value),
                            },
                            {
                              title: "Unite",
                              dataIndex: "unit_short_name",
                              key: "unit_short_name",
                              render: (value) => value || "-",
                            },
                            {
                              title: "Total",
                              dataIndex: "total",
                              key: "total",
                              render: (value) => formatCurrency(value),
                            },
                          ]}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <Title level={5}>Top clients</Title>
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={dailyData.sales?.topCustomers || []}
                          rowKey={(record) => `${record.user_id}-${record.customer_name}`}
                          columns={[
                            { title: "Client", dataIndex: "customer_name", key: "customer_name" },
                            {
                              title: "Total",
                              dataIndex: "total",
                              key: "total",
                              render: (value) => formatCurrency(value),
                            },
                            { title: "Ventes", dataIndex: "orders_count", key: "orders_count" },
                          ]}
                        />
                      </Col>
                    </Row>

                    <Divider />

                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Title level={5}>Stock achete</Title>
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={dailyData.purchases?.products || []}
                          rowKey={(record) => `${record.product_id}-${record.product_name}`}
                          columns={[
                            { title: "Produit", dataIndex: "product_name", key: "product_name" },
                            {
                              title: "Quantite",
                              dataIndex: "quantity",
                              key: "quantity",
                              render: (value) => formatQuantity(value),
                            },
                            {
                              title: "Unite",
                              dataIndex: "unit_short_name",
                              key: "unit_short_name",
                              render: (value) => value || "-",
                            },
                            {
                              title: "Total",
                              dataIndex: "total",
                              key: "total",
                              render: (value) => formatCurrency(value),
                            },
                          ]}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <Title level={5}>Ajustements stock</Title>
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={dailyData.adjustments?.products || []}
                          rowKey={(record) => `${record.product_id}-${record.product_name}`}
                          columns={[
                            {
                              title: "Produit",
                              dataIndex: "product_name",
                              key: "product_name",
                            },
                            {
                              title: "Ajout",
                              dataIndex: "quantity_added",
                              key: "quantity_added",
                              render: (value) => formatQuantity(value),
                            },
                            {
                              title: "Retrait",
                              dataIndex: "quantity_removed",
                              key: "quantity_removed",
                              render: (value) => formatQuantity(value),
                            },
                            {
                              title: "Net",
                              dataIndex: "net_quantity",
                              key: "net_quantity",
                              render: (value) => renderNetQuantity(value),
                            },
                            {
                              title: "Unite",
                              dataIndex: "unit_short_name",
                              key: "unit_short_name",
                              render: (value) => value || "-",
                            },
                          ]}
                        />
                      </Col>
                    </Row>

                    <Divider />

                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Title level={5}>Depenses par categorie</Title>
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={dailyData.expenses?.byCategory || []}
                          rowKey={(record) => `${record.category_name}`}
                          columns={[
                            { title: "Categorie", dataIndex: "category_name", key: "category_name" },
                            {
                              title: "Total",
                              dataIndex: "total",
                              key: "total",
                              render: (value) => formatCurrency(value),
                            },
                            { title: "Nb", dataIndex: "count", key: "count" },
                          ]}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <Title level={5}>Top fournisseurs</Title>
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={dailyData.purchases?.topSuppliers || []}
                          rowKey={(record) => `${record.user_id}-${record.supplier_name}`}
                          columns={[
                            {
                              title: "Fournisseur",
                              dataIndex: "supplier_name",
                              key: "supplier_name",
                            },
                            {
                              title: "Total",
                              dataIndex: "total",
                              key: "total",
                              render: (value) => formatCurrency(value),
                            },
                            { title: "Achats", dataIndex: "orders_count", key: "orders_count" },
                          ]}
                        />
                      </Col>
                    </Row>

                    <Divider />

                    <Title level={5}>Production du jour</Title>
                    <Row gutter={16}>
                      <Col xs={12} md={4} style={{ marginBottom: 12 }}>
                        <Card>
                          <Text type="secondary">Quantite produite</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {formatQuantity(dailyData.production?.totalOutput || 0)}
                          </div>
                        </Card>
                      </Col>
                      <Col xs={12} md={4} style={{ marginBottom: 12 }}>
                        <Card>
                          <Text type="secondary">Cout production</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {formatCurrency(dailyData.production?.totalCost || 0)}
                          </div>
                        </Card>
                      </Col>
                      <Col xs={12} md={4} style={{ marginBottom: 12 }}>
                        <Card>
                          <Text type="secondary">Valeur vente</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {formatCurrency(dailyData.production?.totalSaleValue || 0)}
                          </div>
                        </Card>
                      </Col>
                      <Col xs={12} md={4} style={{ marginBottom: 12 }}>
                        <Card>
                          <Text type="secondary">Marge globale</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {formatCurrency(dailyData.production?.totalMargin || 0)}
                          </div>
                        </Card>
                      </Col>
                      <Col xs={12} md={4} style={{ marginBottom: 12 }}>
                        <Card>
                          <Text type="secondary">Stock restant</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {dailyData.production?.totalAvailableOutput === null ||
                            dailyData.production?.totalAvailableOutput === undefined
                              ? "-"
                              : formatQuantity(dailyData.production.totalAvailableOutput)}
                          </div>
                        </Card>
                      </Col>
                    </Row>

                    <Table
                      size="small"
                      pagination={false}
                      dataSource={dailyData.production?.outputs || []}
                      rowKey={(record) => `${record.product_id}-${record.product_name}`}
                      columns={[
                        {
                          title: "Produit",
                          dataIndex: "product_name",
                          key: "product_name",
                        },
                        {
                          title: "Quantite produite",
                          dataIndex: "produced_quantity",
                          key: "produced_quantity",
                          render: (value) => formatQuantity(value),
                        },
                        {
                          title: "Unite",
                          dataIndex: "unit_short_name",
                          key: "unit_short_name",
                          render: (value) => value || "-",
                        },
                        {
                          title: "Stock restant",
                          dataIndex: "available_output_quantity",
                          key: "available_output_quantity",
                          render: (value) =>
                            value === null || value === undefined ? "-" : formatQuantity(value),
                        },
                        {
                          title: "Valeur vente",
                          dataIndex: "total_sale_value",
                          key: "total_sale_value",
                          render: (value) => formatCurrency(value),
                        },
                        {
                          title: "Cycles",
                          dataIndex: "production_count",
                          key: "production_count",
                        },
                      ]}
                    />
                      </>
                    )}
                  </Card>
                  </div>
                )}
              </>
            ),
          },
          {
            key: "sales_summary",
            label: "Ventes multi-periode",
            children: (
              <>
                <Card>
                  <Row gutter={16} align="middle">
                    <Col xs={24} md={6}>
                      <Text strong>Periode</Text>
                      <RangePicker
                        style={{ width: "100%", marginTop: 4 }}
                        value={salesRange}
                        onChange={(value) => setSalesRange(value)}
                      />
                    </Col>
                    <Col xs={24} md={5}>
                      <Text strong>Regroupement</Text>
                      <Select
                        style={{ width: "100%", marginTop: 4 }}
                        value={salesGroupBy}
                        onChange={(value) => setSalesGroupBy(value)}
                      >
                        <Option value="day">Jour</Option>
                        <Option value="week">Semaine</Option>
                        <Option value="month">Mois</Option>
                      </Select>
                    </Col>
                    <Col xs={24} md={5}>
                      <Text strong>Établissement</Text>
                      <Select
                        style={{ width: "100%", marginTop: 4 }}
                        value={warehouseId || "all"}
                        onChange={(value) => setWarehouseId(value === "all" ? null : value)}
                      >
                        <Option value="all">Tous les établissements</Option>
                        {warehouses.map((warehouse) => (
                          <Option key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} md={8}>
                      <Space style={{ marginTop: 22 }}>
                        <Button
                          type="default"
                          icon={<FileSearchOutlined />}
                          loading={loadingPreviewSales}
                          onClick={previewSalesSummary}
                        >
                          Apercu
                        </Button>
                        {canGenerateReportType("sales_summary") && (
                          <Button
                            type="primary"
                            icon={<FileTextOutlined />}
                            loading={loadingGenerateSales}
                            onClick={generateSalesSummary}
                          >
                            Generer PDF/Excel
                          </Button>
                        )}
                      </Space>
                    </Col>
                  </Row>
                </Card>

                {salesData && (
                  <Card style={{ marginTop: 16 }} title="Synthese ventes">
                    <Row gutter={16}>
                      {salesSummaryCards.map((item) => (
                        <Col xs={12} md={6} key={item.label} style={{ marginBottom: 12 }}>
                          <Card>
                            <Text type="secondary">{item.label}</Text>
                            <div style={{ fontSize: 16, fontWeight: 600 }}>{item.value}</div>
                          </Card>
                        </Col>
                      ))}
                    </Row>

                    <Divider />

                    <Table
                      rowKey={(record) => record.period}
                      dataSource={salesData.periods || []}
                      pagination={{ pageSize: 10 }}
                      columns={[
                        { title: "Periode", dataIndex: "period", key: "period" },
                        {
                          title: "Ventes classiques",
                          dataIndex: "classic_total",
                          key: "classic_total",
                          render: (value) => formatCurrency(value),
                        },
                        {
                          title: "Ventes e-commerce",
                          dataIndex: "ecommerce_total",
                          key: "ecommerce_total",
                          render: (value) => formatCurrency(value),
                        },
                        {
                          title: "Total",
                          dataIndex: "total",
                          key: "total",
                          render: (value) => formatCurrency(value),
                        },
                        { title: "Nb classiques", dataIndex: "classic_count", key: "classic_count" },
                        { title: "Nb e-commerce", dataIndex: "ecommerce_count", key: "ecommerce_count" },
                      ]}
                    />
                  </Card>
                )}
              </>
            ),
          },
          {
            key: "client_product_sales",
            label: "Produits vendus par client",
            children: (
              <>
                <Card>
                  <Row gutter={16}>
                    <Col xs={24} md={6}>
                      <Text strong>Periode</Text>
                      <RangePicker
                        style={{ width: "100%", marginTop: 4 }}
                        value={clientSalesRange}
                        onChange={(value) => setClientSalesRange(value)}
                      />
                    </Col>
                    <Col xs={24} md={4}>
                      <Text strong>Mode</Text>
                      <Select
                        style={{ width: "100%", marginTop: 4 }}
                        value={clientSalesDetailLevel}
                        onChange={(value) => setClientSalesDetailLevel(value)}
                      >
                        <Option value="summary">Resume</Option>
                        <Option value="detailed">Detaille</Option>
                      </Select>
                    </Col>
                    <Col xs={24} md={6}>
                      <Text strong>Clients</Text>
                      <Select
                        mode="multiple"
                        allowClear
                        placeholder="Tous les clients"
                        style={{ width: "100%", marginTop: 4 }}
                        value={clientSalesClientIds}
                        onChange={(value) => setClientSalesClientIds(value)}
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        options={customers.map((customer) => ({
                          label: customer.name || `Client #${customer.id}`,
                          value: customer.id,
                        }))}
                      />
                    </Col>
                    <Col xs={24} md={4}>
                      <Text strong>Type produit</Text>
                      <Select
                        style={{ width: "100%", marginTop: 4 }}
                        value={clientSalesProductType}
                        onChange={(value) => setClientSalesProductType(value)}
                      >
                        <Option value="all">Tous</Option>
                        <Option value="single">Single</Option>
                        <Option value="variable">Variable</Option>
                        <Option value="service">Service</Option>
                        <Option value="combo">Combo</Option>
                      </Select>
                    </Col>
                    <Col xs={24} md={4}>
                      <Text strong>Établissement</Text>
                      <Select
                        style={{ width: "100%", marginTop: 4 }}
                        value={warehouseId || "all"}
                        onChange={(value) => setWarehouseId(value === "all" ? null : value)}
                      >
                        <Option value="all">Tous les établissements</Option>
                        {warehouses.map((warehouse) => (
                          <Option key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24}>
                      <Space style={{ marginTop: 14 }}>
                        <Button
                          type="default"
                          icon={<FileSearchOutlined />}
                          loading={loadingPreviewClientSales}
                          onClick={previewClientProductSales}
                        >
                          Apercu
                        </Button>
                        {canGenerateReportType("client_product_sales") && (
                          <Button
                            type="primary"
                            icon={<FileTextOutlined />}
                            loading={loadingGenerateClientSales}
                            onClick={generateClientProductSales}
                          >
                            Generer PDF/Excel
                          </Button>
                        )}
                      </Space>
                    </Col>
                  </Row>
                </Card>

                {clientSalesData && (
                  <Card style={{ marginTop: 16 }} title="Synthese ventes produits par client">
                    <Text type="secondary">
                      {clientSalesData.meta?.periodLabel
                        ? `Periode: ${clientSalesData.meta.periodLabel}`
                        : ""}
                    </Text>

                    <Row gutter={16} style={{ marginTop: 12 }}>
                      {clientSalesSummaryCards.map((item) => (
                        <Col xs={12} md={6} key={item.label} style={{ marginBottom: 12 }}>
                          <Card>
                            <Text type="secondary">{item.label}</Text>
                            <div style={{ fontSize: 16, fontWeight: 600 }}>{item.value}</div>
                          </Card>
                        </Col>
                      ))}
                    </Row>

                    <Divider />

                    <Table
                      rowKey={(record) =>
                        clientSalesData.meta?.detailLevel === "detailed"
                          ? `${record.order_id}-${record.product_id}-${record.order_date}`
                          : `${record.client_id ?? "walkin"}-${record.product_id}`
                      }
                      dataSource={clientSalesData.rows || []}
                      pagination={{ pageSize: 10 }}
                      columns={
                        clientSalesData.meta?.detailLevel === "detailed"
                          ? [
                              { title: "Date", dataIndex: "order_date", key: "order_date" },
                              { title: "Facture", dataIndex: "invoice_number", key: "invoice_number" },
                              { title: "Client", dataIndex: "client_name", key: "client_name" },
                              { title: "Produit", dataIndex: "product_name", key: "product_name" },
                              { title: "Categorie", dataIndex: "category_name", key: "category_name" },
                              { title: "Type", dataIndex: "product_type", key: "product_type" },
                              {
                                title: "Quantite",
                                dataIndex: "quantity",
                                key: "quantity",
                                render: (value) => formatQuantity(value),
                              },
                              {
                                title: "Prix unitaire",
                                dataIndex: "unit_price",
                                key: "unit_price",
                                render: (value) => formatCurrency(value),
                              },
                              {
                                title: "Total",
                                dataIndex: "subtotal",
                                key: "subtotal",
                                render: (value) => formatCurrency(value),
                              },
                            ]
                          : [
                              { title: "Client", dataIndex: "client_name", key: "client_name" },
                              { title: "Produit", dataIndex: "product_name", key: "product_name" },
                              { title: "Categorie", dataIndex: "category_name", key: "category_name" },
                              { title: "Type", dataIndex: "product_type", key: "product_type" },
                              {
                                title: "Quantite",
                                dataIndex: "quantity",
                                key: "quantity",
                                render: (value) => formatQuantity(value),
                              },
                              {
                                title: "Prix moyen",
                                dataIndex: "avg_unit_price",
                                key: "avg_unit_price",
                                render: (value) => formatCurrency(value),
                              },
                              {
                                title: "Total",
                                dataIndex: "amount",
                                key: "amount",
                                render: (value) => formatCurrency(value),
                              },
                              { title: "Nb ventes", dataIndex: "sales_count", key: "sales_count" },
                              {
                                title: "Periode vente",
                                key: "sale_range",
                                render: (_, record) =>
                                  record.first_sale_date && record.last_sale_date
                                    ? `${record.first_sale_date} au ${record.last_sale_date}`
                                    : "-",
                              },
                            ]
                      }
                    />
                  </Card>
                )}
              </>
            ),
          },
          {
            key: "profitability",
            label: "Rentabilite / marge",
            children: (
              <>
                <Card>
                  <Row gutter={16} align="middle">
                    <Col xs={24} md={6}>
                      <Text strong>Periode</Text>
                      <RangePicker
                        style={{ width: "100%", marginTop: 4 }}
                        value={profitRange}
                        onChange={(value) => setProfitRange(value)}
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <Text strong>Établissement</Text>
                      <Select
                        style={{ width: "100%", marginTop: 4 }}
                        value={warehouseId || "all"}
                        onChange={(value) => setWarehouseId(value === "all" ? null : value)}
                      >
                        <Option value="all">Tous les établissements</Option>
                        {warehouses.map((warehouse) => (
                          <Option key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} md={12}>
                      <Space style={{ marginTop: 22 }}>
                        <Button
                          type="default"
                          icon={<FileSearchOutlined />}
                          loading={loadingPreviewProfit}
                          onClick={previewProfitability}
                        >
                          Apercu
                        </Button>
                        {canGenerateReportType("profitability") && (
                          <Button
                            type="primary"
                            icon={<FileTextOutlined />}
                            loading={loadingGenerateProfit}
                            onClick={generateProfitability}
                          >
                            Generer PDF/Excel
                          </Button>
                        )}
                      </Space>
                    </Col>
                  </Row>
                </Card>

                {profitData && (
                  <Card style={{ marginTop: 16 }} title="Synthese marge">
                    <Text type="secondary">
                      Meme definition canonique que le tableau de bord et le compte de
                      resultat : revenus nets reconnus moins cout des revenus
                      correspondants.
                    </Text>
                    <Row gutter={16} style={{ marginTop: 12 }}>
                      {profitSummaryCards.map((item) => (
                        <Col xs={12} md={6} key={item.label} style={{ marginBottom: 12 }}>
                          <Card>
                            <Text type="secondary">{item.label}</Text>
                            <div style={{ fontSize: 16, fontWeight: 600 }}>{item.value}</div>
                          </Card>
                        </Col>
                      ))}
                    </Row>

                    <Divider />

                    <Table
                      rowKey={(record) => record.entity_key || record.product_id}
                      dataSource={profitData.items || []}
                      pagination={{ pageSize: 10 }}
                      columns={[
                        { title: "Produit", dataIndex: "product_name", key: "product_name" },
                        { title: "Categorie", dataIndex: "category_name", key: "category_name" },
                        {
                          title: "Quantite",
                          dataIndex: "quantity",
                          key: "quantity",
                          render: (value) => formatQuantity(value),
                        },
                        {
                          title: "Chiffre d'affaires",
                          dataIndex: "revenue",
                          key: "revenue",
                          render: (value) => formatCurrency(value),
                        },
                        {
                          title: "Cout du revenu",
                          dataIndex: "cogs",
                          key: "cogs",
                          render: (value) => formatCurrency(value),
                        },
                        {
                          title: "Marge",
                          dataIndex: "margin",
                          key: "margin",
                          render: (value) => formatCurrency(value),
                        },
                        {
                          title: "Taux",
                          dataIndex: "margin_rate",
                          key: "margin_rate",
                          render: (value) => formatPercent(value),
                        },
                      ]}
                    />
                  </Card>
                )}
              </>
            ),
          },
          {
            key: "stock_valuation",
            label: "Valorisation stock",
            children: (
              <>
                <Card>
                  <Row gutter={16} align="middle">
                    <Col xs={24} md={6}>
                      <Text strong>Periode rotation</Text>
                      <RangePicker
                        style={{ width: "100%", marginTop: 4 }}
                        value={stockRange}
                        onChange={(value) => setStockRange(value)}
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <Text strong>Établissement</Text>
                      <Select
                        style={{ width: "100%", marginTop: 4 }}
                        value={warehouseId || "all"}
                        onChange={(value) => setWarehouseId(value === "all" ? null : value)}
                      >
                        <Option value="all">Tous les établissements</Option>
                        {warehouses.map((warehouse) => (
                          <Option key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} md={12}>
                      <Space style={{ marginTop: 22 }}>
                        <Button
                          type="default"
                          icon={<FileSearchOutlined />}
                          loading={loadingPreviewStock}
                          onClick={previewStockValuation}
                        >
                          Apercu
                        </Button>
                        {canGenerateReportType("stock_valuation") && (
                          <Button
                            type="primary"
                            icon={<FileTextOutlined />}
                            loading={loadingGenerateStock}
                            onClick={generateStockValuation}
                          >
                            Generer PDF/Excel
                          </Button>
                        )}
                      </Space>
                    </Col>
                  </Row>
                </Card>

                {stockData && (
                  <Card style={{ marginTop: 16 }} title="Synthese stock">
                    <Row gutter={16}>
                      {stockSummaryCards.map((item) => (
                        <Col xs={12} md={6} key={item.label} style={{ marginBottom: 12 }}>
                          <Card>
                            <Text type="secondary">{item.label}</Text>
                            <div style={{ fontSize: 16, fontWeight: 600 }}>{item.value}</div>
                          </Card>
                        </Col>
                      ))}
                    </Row>

                    <Divider />

                    <Table
                      rowKey={(record) => record.product_id}
                      dataSource={stockData.items || []}
                      pagination={{ pageSize: 10 }}
                      columns={[
                        { title: "Produit", dataIndex: "product_name", key: "product_name" },
                        { title: "Categorie", dataIndex: "category_name", key: "category_name" },
                        {
                          title: "Stock",
                          dataIndex: "current_stock",
                          key: "current_stock",
                          render: (value) => formatQuantity(value),
                        },
                        {
                          title: "Valeur cout",
                          dataIndex: "stock_value_cost",
                          key: "stock_value_cost",
                          render: (value) => formatCurrency(value),
                        },
                        {
                          title: "Valeur vente",
                          dataIndex: "stock_value_sale",
                          key: "stock_value_sale",
                          render: (value) => formatCurrency(value),
                        },
                        {
                          title: "Ventes periode",
                          dataIndex: "sold_quantity",
                          key: "sold_quantity",
                          render: (value) => formatQuantity(value),
                        },
                        {
                          title: "Rotation",
                          dataIndex: "rotation_rate",
                          key: "rotation_rate",
                          render: (value) => formatPercent(value),
                        },
                      ]}
                    />
                  </Card>
                )}
              </>
            ),
          },
          {
            key: "income_statement",
            label: "Compte de resultat",
            children: (
              <>
                <Card>
                  <Row gutter={16} align="middle">
                    <Col xs={24} md={6}>
                      <Text strong>Periode</Text>
                      <RangePicker
                        style={{ width: "100%", marginTop: 4 }}
                        value={incomeRange}
                        onChange={(value) => setIncomeRange(value)}
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <Text strong>Établissement</Text>
                      <Select
                        style={{ width: "100%", marginTop: 4 }}
                        value={warehouseId || "all"}
                        onChange={(value) => setWarehouseId(value === "all" ? null : value)}
                      >
                        <Option value="all">Tous les établissements</Option>
                        {warehouses.map((warehouse) => (
                          <Option key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} md={12}>
                      <Space style={{ marginTop: 22 }}>
                        <Button
                          type="default"
                          icon={<FileSearchOutlined />}
                          loading={loadingPreviewIncome}
                          onClick={previewIncomeStatement}
                        >
                          Apercu
                        </Button>
                        {canGenerateReportType("income_statement") && (
                          <Button
                            type="primary"
                            icon={<FileTextOutlined />}
                            loading={loadingGenerateIncome}
                            onClick={generateIncomeStatement}
                          >
                            Generer PDF/Excel
                          </Button>
                        )}
                      </Space>
                    </Col>
                  </Row>
                </Card>

                {incomeData && (
                  <Card style={{ marginTop: 16 }} title="Synthèse du compte de résultat">
                    <IncomeStatementStockAdjustments data={incomeData.annexes?.stockAdjustments} currency={incomeData.meta?.currency} />
                    <Text type="secondary">
                      La marge brute correspond au chiffre d'affaires net diminué du coût
                      directement lié à chaque vente. Les achats et la variation de stock
                      sont présentés séparément comme indicateurs de contrôle et ne sont
                      pas déduits une seconde fois.
                    </Text>
                    <Row gutter={16} style={{ marginTop: 12 }}>
                      {incomeSummaryCards.map((item) => (
                        <Col xs={12} md={6} key={item.label} style={{ marginBottom: 12 }}>
                          <Card>
                            <Text type="secondary">{item.label}</Text>
                            <div style={{ fontSize: 16, fontWeight: 600 }}>{item.value}</div>
                          </Card>
                        </Col>
                      ))}
                    </Row>

                    <Divider />

                    <Title level={5}>Compte de résultat détaillé</Title>
                    <Table
                      rowKey={(record) => record.key}
                      pagination={false}
                      dataSource={incomeData.statementRows || []}
                      columns={[
                        {
                          title: "Rubrique",
                          dataIndex: "label",
                          key: "label",
                          render: (value, record) => {
                            const isHeader = record?.rowType === "header";
                            const isStrong =
                              record?.rowType === "total" || record?.rowType === "grand_total";
                            if (isHeader) {
                              return <Tag color="blue">{String(value || "")}</Tag>;
                            }
                            return <Text strong={isStrong}>{value}</Text>;
                          },
                        },
                        {
                          title: "Charges",
                          dataIndex: "charges",
                          key: "charges",
                          align: "right",
                          render: (value, record) => (
                            <Text
                              strong={
                                record?.rowType === "total" || record?.rowType === "grand_total"
                              }
                            >
                              {record?.rowType === "header" || !Number(value)
                                ? "-"
                                : formatCurrency(value)}
                            </Text>
                          ),
                        },
                        {
                          title: "Produits",
                          dataIndex: "produits",
                          key: "produits",
                          align: "right",
                          render: (value, record) => (
                            <Text
                              strong={
                                record?.rowType === "total" || record?.rowType === "grand_total"
                              }
                            >
                              {record?.rowType === "header" || !Number(value)
                                ? "-"
                                : formatCurrency(value)}
                            </Text>
                          ),
                        },
                      ]}
                    />

                    <Divider />

                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Title level={5}>Produits ordinaires</Title>
                        <Table
                          size="small"
                          pagination={false}
                          rowKey={(record) => record.key || record.label || record.channel}
                          dataSource={
                            incomeData.breakdown?.ordinaryProducts ||
                            incomeData.workflow?.revenueChannels ||
                            []
                          }
                          columns={[
                            {
                              title: "Libelle",
                              key: "label",
                              render: (_, record) => record.label || record.channel || "-",
                            },
                            {
                              title: "Montant",
                              dataIndex: "amount",
                              key: "amount",
                              render: (value) => formatCurrency(value),
                            },
                            {
                              title: "Nb",
                              key: "lines_count",
                              render: (_, record) =>
                                record.lines_count ?? record.orders_count ?? 0,
                            },
                          ]}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <Title level={5}>Coût des revenus</Title>
                        <Table
                          size="small"
                          pagination={false}
                          rowKey={(record) => record.key || record.label}
                          dataSource={incomeData.breakdown?.directCharges || []}
                          columns={[
                            { title: "Libelle", dataIndex: "label", key: "label" },
                            {
                              title: "Montant",
                              dataIndex: "amount",
                              key: "amount",
                              render: (value) => formatCurrency(value),
                            },
                            {
                              title: "Infos",
                              key: "meta",
                              render: (_, record) =>
                                record.quantity
                                  ? `${formatQuantity(record.quantity)} / ${record.count || 0}`
                                  : record.count || "-",
                            },
                          ]}
                        />
                        {(incomeData.breakdown?.costingCoverage || []).length > 0 && (
                          <>
                            <Divider style={{ margin: "12px 0" }} />
                            <Text type="secondary">
                              Part des lignes avec un coût documenté :{" "}
                              {formatPercent(
                                incomeData.summary?.costCoverage?.traceableCostLineRate ??
                                  incomeData.summary?.costCoverage?.snapshotLineRate ??
                                  incomeData.summary?.merchandiseSnapshotCoverageRate ??
                                  0
                              )}{" "}
                              ({incomeData.summary?.costCoverage?.traceableCostLines ??
                                incomeData.summary?.costCoverage?.snapshotLines ??
                                incomeData.summary?.merchandiseSnapshotLines ??
                                0}/
                              {incomeData.summary?.costCoverage?.lineCount ??
                                incomeData.summary?.merchandiseCostLines ??
                                0})
                            </Text>
                            <Table
                              style={{ marginTop: 8 }}
                              size="small"
                              pagination={false}
                              rowKey={(record) => record.key || record.label}
                              dataSource={incomeData.breakdown?.costingCoverage || []}
                              columns={[
                                { title: "Mode de valorisation", dataIndex: "label", key: "label" },
                                {
                                  title: "Montant net",
                                  dataIndex: "amount",
                                  key: "amount",
                                  render: (value) => formatCurrency(value),
                                },
                                {
                                  title: "Lignes",
                                  dataIndex: "count",
                                  key: "count",
                                  render: (value) => value || 0,
                                },
                              ]}
                            />
                          </>
                        )}
                      </Col>
                    </Row>

                    <Divider />

                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Title level={5}>Charges d'exploitation par categorie</Title>
                        <Table
                          size="small"
                          pagination={false}
                          rowKey={(record) => `${record.category_name}`}
                          dataSource={
                            incomeData.breakdown?.operatingExpenses ||
                            incomeData.workflow?.expensesByCategory ||
                            []
                          }
                          columns={[
                            { title: "Categorie", dataIndex: "category_name", key: "category_name" },
                            {
                              title: "Montant",
                              dataIndex: "total",
                              key: "total",
                              render: (value) => formatCurrency(value),
                            },
                            { title: "Nb", dataIndex: "count", key: "count" },
                          ]}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <Title level={5}>Tresorerie (annexe hors resultat)</Title>
                        <Table
                          size="small"
                          pagination={false}
                          rowKey={(record) => record.key}
                          dataSource={[
                            {
                              key: "linked_sales_in",
                              label: "Encaissements lies ventes",
                              amount:
                                incomeData.annexes?.treasury?.linked_sales_in ||
                                incomeData.workflow?.payments?.linked_sales_in ||
                                0,
                            },
                            {
                              key: "linked_sales_out",
                              label: "Decaissements lies ventes",
                              amount: -(
                                incomeData.annexes?.treasury?.linked_sales_out ||
                                incomeData.workflow?.payments?.linked_sales_out ||
                                0
                              ),
                            },
                            {
                              key: "linked_purchases_out",
                              label: "Decaissements lies achats",
                              amount: -(
                                incomeData.annexes?.treasury?.linked_purchases_out ||
                                incomeData.workflow?.payments?.linked_purchases_out ||
                                0
                              ),
                            },
                            {
                              key: "expense_payments_out",
                              label: "Decaissements lies depenses",
                              amount: -(
                                incomeData.annexes?.treasury?.expense_payments_out ||
                                incomeData.workflow?.payments?.expense_payments_out ||
                                0
                              ),
                            },
                            {
                              key: "unlinked_in",
                              label: "Encaissements non rattaches",
                              amount:
                                incomeData.annexes?.treasury?.unlinked_in ||
                                incomeData.workflow?.payments?.unlinked_in ||
                                0,
                            },
                            {
                              key: "unlinked_out",
                              label: "Decaissements non rattaches",
                              amount: -(
                                incomeData.annexes?.treasury?.unlinked_out ||
                                incomeData.workflow?.payments?.unlinked_out ||
                                0
                              ),
                            },
                          ]}
                          columns={[
                            { title: "Flux", dataIndex: "label", key: "label" },
                            {
                              title: "Montant",
                              dataIndex: "amount",
                              key: "amount",
                              render: (value) => formatCurrency(value),
                            },
                          ]}
                        />
                      </Col>
                    </Row>

                    <Divider />

                    <Row gutter={16}>
                      <Col xs={24} md={6}>
                        <Card>
                          <Text type="secondary">Achats periode</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {formatCurrency(
                              incomeData.annexes?.purchases?.total ||
                                incomeData.workflow?.purchases?.total ||
                                0
                            )}
                          </div>
                        </Card>
                      </Col>
                      <Col xs={24} md={6}>
                        <Card>
                          <Text type="secondary">Creances clients</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {formatCurrency(
                              incomeData.annexes?.receivables?.clients_due_total ||
                                incomeData.workflow?.receivables?.clients_due_total ||
                                0
                            )}
                          </div>
                        </Card>
                      </Col>
                      <Col xs={24} md={6}>
                        <Card>
                          <Text type="secondary">Dettes fournisseurs</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {formatCurrency(
                              incomeData.annexes?.receivables?.suppliers_due_total ||
                                incomeData.workflow?.receivables?.suppliers_due_total ||
                                0
                            )}
                          </div>
                        </Card>
                      </Col>
                      <Col xs={24} md={6}>
                        <Card>
                          <Text type="secondary">Tresorerie nette</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {formatCurrency(
                              incomeData.annexes?.treasury?.net_cash ||
                                incomeData.summary?.treasuryNetCash ||
                                0
                            )}
                          </div>
                        </Card>
                      </Col>
                    </Row>

                    <Row gutter={16} style={{ marginTop: 16 }}>
                      <Col xs={24} md={6}>
                        <Card>
                          <Text type="secondary">Stock ouverture periode (cout)</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {formatCurrency(
                              incomeData.annexes?.stockValuation?.opening_cost ||
                                incomeData.summary?.stockOpeningValue ||
                                0
                            )}
                          </div>
                        </Card>
                      </Col>
                      <Col xs={24} md={6}>
                        <Card>
                          <Text type="secondary">Stock cloture periode (cout)</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {formatCurrency(
                              incomeData.annexes?.stockValuation?.closing_cost ||
                                incomeData.summary?.stockValueCost ||
                                0
                            )}
                          </div>
                        </Card>
                      </Col>
                      <Col xs={24} md={6}>
                        <Card>
                          <Text type="secondary">Variation de stock (cout)</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {formatCurrency(
                              incomeData.annexes?.stockValuation?.variation_cost ||
                                incomeData.summary?.stockVariationValue ||
                                0
                            )}
                          </div>
                        </Card>
                      </Col>
                      <Col xs={24} md={6}>
                        <Card>
                          <Text type="secondary">Stock cloture periode (vente)</Text>
                          <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {formatCurrency(
                              incomeData.annexes?.stockValuation?.closing_sale ||
                                incomeData.summary?.stockValueSale ||
                                0
                            )}
                          </div>
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                )}
              </>
            ),
          },
        ].filter((item) => canViewReportType(item.key))}
      />

      <Card
        style={{ marginTop: 16 }}
        title={`Historique des rapports generes - ${reportTypeLabel(activeTab)}`}
      >
        <Table
          rowKey={(record) => record.id}
          dataSource={reports}
          columns={reportColumns}
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
};

export default CommercialReports;
